import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import Layout from '../components/Layout';
import Container from '../components/Container';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageWrapper = styled.div`
  padding: 7rem 0 4rem;
  background: linear-gradient(135deg, #0e1633 0%, #1e2a4a 100%);
  min-height: 85vh;
  animation: ${fadeIn} 0.8s ease-out;
`;

const StatusContainer = styled.div`
  background: rgba(20, 30, 60, 0.85);
  border-radius: 24px;
  box-shadow: 0 8px 32px 0 #2d6cff33;
  border: 1.5px solid #2d6cff44;
  padding: 2.5rem 2rem;
  max-width: 600px;
  margin: 0 auto;
  animation: ${fadeIn} 1s ease-out 0.2s both;
  color: #fff;
`;

const StatusHeader = styled.h1`
  font-size: 2.2rem;
  margin-bottom: 2rem;
  text-align: center;
  color: #6ea8ff;
  text-shadow: 0 0 16px #2d6cff, 0 2px 10px #0008;
`;

const StatusInfo = styled.div`
  margin-bottom: 2rem;
`;

const StatusItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 1rem 0;
  border-bottom: 1px solid #2d6cff22;
  color: #b6d0ff;
  &:last-child {
    border-bottom: none;
  }
`;

const StatusLabel = styled.span`
  font-weight: 600;
  color: #6ea8ff;
`;

const StatusValue = styled.span`
  font-weight: 500;
  color: #fff;
`;

const StatusProgress = styled.div`
  margin: 2rem 0;
`;

const ProgressBar = styled.div`
  height: 10px;
  background: #1e2a4a;
  border-radius: 5px;
  margin-top: 1rem;
  overflow: hidden;
  box-shadow: 0 2px 8px #2d6cff22;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${props => props.progress}%;
  background: ${props => {
    if (props.status === 'waiting') return 'linear-gradient(90deg, #f7b731 0%, #ffe066 100%)';
    if (props.status === 'confirming') return 'linear-gradient(90deg, #2d6cff 0%, #3a8fff 100%)';
    if (props.status === 'finished') return 'linear-gradient(90deg, #2ecc71 0%, #27ae60 100%)';
    return '#2d6cff';
  }};
  transition: width 0.5s ease, background 0.5s ease;
`;

const StatusMessage = styled.div`
  background: ${props => {
    if (props.status === 'waiting') return 'linear-gradient(90deg, #1e2a4a 60%, #f7b73122 100%)';
    if (props.status === 'confirming') return 'linear-gradient(90deg, #1e2a4a 60%, #2d6cff22 100%)';
    if (props.status === 'finished') return 'linear-gradient(90deg, #1e2a4a 60%, #2ecc7122 100%)';
    return 'rgba(44, 108, 255, 0.08)';
  }};
  color: ${props => {
    if (props.status === 'waiting') return '#ffe066';
    if (props.status === 'confirming') return '#6ea8ff';
    if (props.status === 'finished') return '#2ecc71';
    return '#b6d0ff';
  }};
  padding: 1.2rem;
  border-radius: 10px;
  text-align: center;
  font-weight: 500;
  margin-top: 1.5rem;
  font-size: 1.08rem;
  box-shadow: 0 2px 12px #2d6cff22;
`;

const PaymentAddress = styled.div`
  background: rgba(30, 42, 74, 0.85);
  border: 1.5px dashed #2d6cff88;
  border-radius: 12px;
  padding: 1.2rem;
  margin: 1.5rem 0;
  text-align: center;
  word-break: break-all;
  font-family: monospace;
  font-size: 1rem;
  color: #fff;
`;

const CopyButton = styled.button`
  background: linear-gradient(90deg, #2d6cff 0%, #3a8fff 100%);
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1.2rem;
  font-size: 0.95rem;
  margin-top: 0.7rem;
  color: #fff;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px #2d6cff44;
  &:hover {
    background: linear-gradient(90deg, #3a8fff 0%, #2d6cff 100%);
    color: #fff;
    transform: translateY(-2px) scale(1.04);
  }
`;

function StatusPage() {
  const { paymentId } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('waiting'); // waiting, confirming, finished
  const [progress, setProgress] = useState(30);
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [confirmations, setConfirmations] = useState(0);
  
  // Function to copy payment address to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  
  // Check for early confirmations using our custom blockchain tracker
  const checkEarlyConfirmations = async () => {
    if (!paymentId) return;
    
    try {
      const response = await fetch(`/api/payments/${paymentId}/confirmations`);
      const data = await response.json();
      
      if (data.success) {
        // If early_confirmed or other confirmed status, redirect to success page
        if (data.status === 'early_confirmed' || data.confirmed === true) {
          console.log('Early confirmation detected, redirecting to success page');
          navigate('/success', { 
            state: { 
              licenseKey: data.license_key,
              paymentId: paymentId,
              planId: paymentData?.plan_id
            } 
          });
          return;
        }
        
        // Otherwise update confirmation count
        if (data.confirmations && typeof data.confirmations === 'number') {
          setConfirmations(data.confirmations);
        }
      }
    } catch (err) {
      console.error('Error checking early confirmations:', err);
    }
  };
  
  useEffect(() => {
    const checkPaymentStatus = async () => {
      try {
        const response = await fetch(`/api/payments/${paymentId}/status`);
        const data = await response.json();
        
        if (data.success) {
          setPaymentData(data.payment);
          setStatus(data.payment.status);
          
          // Set progress based on status
          if (data.payment.status === 'waiting') setProgress(30);
          else if (data.payment.status === 'confirming') setProgress(70);
          else if (data.payment.status === 'finished' || data.payment.status === 'confirmed' || data.payment.status === 'early_confirmed') setProgress(100);
          
          // Navigate to success page immediately when payment is confirmed
          if (data.payment.status === 'finished' || data.payment.status === 'confirmed' || data.payment.status === 'early_confirmed') {
            if (data.payment.license_key) {
              navigate('/success', { 
                state: { 
                  licenseKey: data.payment.license_key,
                  paymentId: data.payment.payment_id,
                  planId: data.payment.plan_id
                } 
              });
              return; // Exit early to prevent setting loading to false
            }
          }
          
          // Only check for early confirmations if payment status is still pending
          // and we have a transaction hash
          if ((data.payment.status === 'waiting' || data.payment.status === 'confirming') && 
              data.payment.payin_hash) {
            // Call our custom confirmation check endpoint
            checkEarlyConfirmations();
          }
          
          setLoading(false);
        } else {
          setError(data.message || 'Failed to fetch payment status');
          setLoading(false);
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        setError('Network error. Please refresh the page.');
        setLoading(false);
      }
    };
    
    // Check immediately
    checkPaymentStatus();
    
    // Then set up polling every 5 seconds for status updates
    const intervalId = setInterval(() => {
      checkPaymentStatus();
    }, 5000);
    
    // Clean up interval when component unmounts
    return () => clearInterval(intervalId);
  }, [paymentId, navigate, paymentData?.plan_id]);
  
  const getStatusMessage = () => {
    // If we have confirmations from blockchain but NOWPayments status is still waiting
    if (confirmations > 0 && (status === 'waiting' || status === 'confirming')) {
      return `Payment detected with ${confirmations} confirmation${confirmations !== 1 ? 's' : ''}! Waiting for payment processor to update... (Usually requires 3-6 confirmations)`;
    }
    
    const isLifetimePlan = paymentData?.plan_id === 'lifetime';
    
    switch(status) {
      case 'waiting':
        return `Waiting for payment... Please send the exact amount specified above to complete your ${isLifetimePlan ? 'lifetime license' : 'purchase'}.`;
      case 'confirming':
        return `Payment received and is being confirmed on the blockchain! (${confirmations} confirmation${confirmations !== 1 ? 's' : ''} so far)`;
      case 'partially_paid':
        return 'Payment received but amount is incorrect. Please contact me on Telegram for assistance.';
      case 'finished':
      case 'confirmed':
      case 'early_confirmed':
        return `Payment fully confirmed! ${isLifetimePlan ? 'Your lifetime license is ready. ' : ''}You will be redirected to download page in a few seconds...`;
      case 'failed':
        return 'Payment failed. Please contact me on Telegram for immediate assistance.';
      case 'refunded':
        return 'Payment was refunded. Please contact me on Telegram if you still want to complete your purchase.';
      default:
        return 'Processing payment... This may take a few minutes depending on network congestion.';
    }
  };
  
  if (loading) {
    return (
      <Layout>
        <PageWrapper>
          <Container>
            <StatusContainer>
              <StatusHeader>Loading Payment Details...</StatusHeader>
              <LoadingIndicator />
            </StatusContainer>
          </Container>
        </PageWrapper>
      </Layout>
    );
  }
  
  if (error) {
    return (
      <Layout>
        <PageWrapper>
          <Container>
            <StatusContainer>
              <StatusHeader>Error</StatusHeader>
              <ErrorMessage>{error}</ErrorMessage>
            </StatusContainer>
          </Container>
        </PageWrapper>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <PageWrapper>
        <Container>
          <StatusContainer>
            <StatusHeader>Payment Status</StatusHeader>
            
            <StatusInfo>
              <StatusItem>
                <StatusLabel>Payment ID:</StatusLabel>
                <StatusValue>{paymentId}</StatusValue>
              </StatusItem>
              {paymentData && (
                <>
                  {paymentData.created_at && (
                    <StatusItem>
                      <StatusLabel>Created:</StatusLabel>
                      <StatusValue>
                        {new Date(paymentData.created_at).toLocaleString()}
                      </StatusValue>
                    </StatusItem>
                  )}
                  <StatusItem>
                    <StatusLabel>Status:</StatusLabel>
                    <StatusValue>{status}</StatusValue>
                  </StatusItem>
                  
                  {/* Display plan type if available */}
                  {paymentData.plan_id && (
                    <StatusItem>
                      <StatusLabel>Plan:</StatusLabel>
                      <StatusValue>
                        {paymentData.plan_id === 'lifetime' 
                          ? 'Lifetime License' 
                          : paymentData.plan_id.charAt(0).toUpperCase() + paymentData.plan_id.slice(1)}
                        {paymentData.plan_id === 'lifetime' && paymentData.price_amount === 250 && (
                          <span style={{ color: '#ff5252', marginLeft: '5px', fontWeight: 'bold' }}>
                            (Early Access)
                          </span>
                        )}
                      </StatusValue>
                    </StatusItem>
                  )}
                  
                  {/* Display expiration date for active payments */}
                  {(status === 'finished' || status === 'confirmed' || status === 'early_confirmed') && paymentData.expires_at && (
                    <StatusItem>
                      <StatusLabel>Valid Until:</StatusLabel>
                      <StatusValue>
                        {paymentData.plan_id === 'lifetime' 
                          ? 'Never Expires (Lifetime)' 
                          : new Date(paymentData.expires_at).toLocaleDateString()}
                      </StatusValue>
                    </StatusItem>
                  )}
                  
                  {/* Display payment amount if available */}
                  {paymentData.pay_amount && (
                    <StatusItem>
                      <StatusLabel>Amount to pay:</StatusLabel>
                      <StatusValue>
                        {paymentData.pay_amount} {paymentData.pay_currency?.toUpperCase()}
                      </StatusValue>
                    </StatusItem>
                  )}
                  
                  {/* Display price equivalent */}
                  {paymentData.price_amount && (
                    <StatusItem>
                      <StatusLabel>Price equivalent:</StatusLabel>
                      <StatusValue>
                        {paymentData.price_amount} {paymentData.price_currency?.toUpperCase()}
                      </StatusValue>
                    </StatusItem>
                  )}
                </>
              )}
              {/* Add confirmation count if available */}
              {confirmations > 0 && status !== 'finished' && status !== 'confirmed' && status !== 'early_confirmed' && (
                <StatusItem>
                  <StatusLabel>Blockchain Confirmations:</StatusLabel>
                  <StatusValue>{confirmations}</StatusValue>
                </StatusItem>
              )}
            </StatusInfo>
            
            {/* Display payment address if available and status is waiting */}
            {paymentData && paymentData.pay_address && (
              <>
                <h3>Send payment to:</h3>
                
                <PaymentAddress>
                  {paymentData.pay_address}
                  <br />
                  <CopyButton onClick={() => copyToClipboard(paymentData.pay_address)}>
                    {copied ? 'Copied!' : 'Copy Address'}
                  </CopyButton>
                </PaymentAddress>
                
                <p style={{ textAlign: 'center', margin: '1rem 0' }}>
                  Please send exactly <strong>{paymentData.pay_amount} {paymentData.pay_currency?.toUpperCase()}</strong> to the address above.
                </p>
              </>
            )}
            
            {/* If there's a payment URL provided, add a button to open it */}
            {paymentData && paymentData.payment_url && (
              <div style={{ textAlign: 'center', margin: '1rem 0' }}>
                <a href={paymentData.payment_url} target="_blank" rel="noopener noreferrer">
                  <Button primary fullWidth>Open Payment Page</Button>
                </a>
              </div>
            )}
            
            <StatusProgress>
              <h3>Payment Progress</h3>
              <ProgressBar>
                <ProgressFill progress={progress} status={status} />
              </ProgressBar>
            </StatusProgress>
            
            <StatusMessage status={status}>
              {getStatusMessage()}
            </StatusMessage>
          </StatusContainer>
        </Container>
      </PageWrapper>
    </Layout>
  );
}

const Button = styled.button`
  display: inline-block;
  padding: 0.75rem 1.5rem;
  background-color: ${props => props.primary ? '#0070f3' : '#f3f4f6'};
  color: ${props => props.primary ? 'white' : '#333'};
  border: 1px solid ${props => props.primary ? '#0070f3' : '#d1d5db'};
  border-radius: 5px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  width: ${props => props.fullWidth ? '100%' : 'auto'};
  
  &:hover {
    background-color: ${props => props.primary ? '#0051a8' : '#e5e7eb'};
  }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const LoadingIndicator = styled.div`
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: ${spin} 1s linear infinite;
  margin: 20px auto;
`;

const ErrorMessage = styled.div`
  background-color: #fee2e2;
  color: #ef4444;
  padding: 1rem;
  border-radius: 5px;
  margin-top: 1rem;
  text-align: center;
`;

export default StatusPage; 